// FaqPage.tsx — Public FAQ page (Support Q&A, public tier)
// Reads GET /support/faq (unauthenticated). Endpoint returns only is_public rows.
import React, { useEffect, useState } from 'react';
import {
    Box, Container, Typography, Card, CardContent, CircularProgress,
    AppBar, Toolbar, IconButton, Accordion, AccordionSummary, AccordionDetails,
    TextField, InputAdornment,
} from '@mui/material';
import { ArrowBack, ExpandMore, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TEAL = '#2D6E6A';
const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/dev';

interface FaqItem {
    faq_id: number;
    question: string;
    answer: string;
    category?: string | null;
}

const FaqPage: React.FC = () => {
    const navigate = useNavigate();
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await fetch(`${API_URL}/support/faq`);
                const data = await res.json();
                if (!active) return;
                if (data && data.success && Array.isArray(data.faqs)) {
                    setFaqs(data.faqs);
                } else {
                    setError('We could not load the FAQ right now. Please try again later.');
                }
            } catch (e) {
                if (active) setError('We could not load the FAQ right now. Please try again later.');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    const q = query.trim().toLowerCase();
    const visibleFaqs = q
        ? faqs.filter((f) =>
            f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q))
        : faqs;

    // Group by category (preserves server sort_order within each group).
    const grouped: { category: string; items: FaqItem[] }[] = [];
    for (const item of visibleFaqs) {
        const cat = (item.category && item.category.trim()) || 'General';
        let bucket = grouped.find((g) => g.category === cat);
        if (!bucket) { bucket = { category: cat, items: [] }; grouped.push(bucket); }
        bucket.items.push(item);
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #eee' }}>
                <Toolbar>
                    <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: TEAL }} aria-label="Go back">
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" sx={{ color: TEAL, fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
                        Percoro
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontFamily: 'Poppins, sans-serif' }}>
                    Frequently Asked Questions
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    Answers to common questions about Percoro. Still need help?{' '}
                    <Typography
                        component="a"
                        onClick={() => navigate('/contact')}
                        sx={{ color: TEAL, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Contact us
                    </Typography>.
                </Typography>

                {!loading && !error && faqs.length > 0 && (
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search FAQs"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        sx={{ mb: 3 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: TEAL }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: TEAL }} />
                    </Box>
                )}

                {!loading && error && (
                    <Card elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2 }}>
                        <CardContent>
                            <Typography color="text.secondary">{error}</Typography>
                        </CardContent>
                    </Card>
                )}

                {!loading && !error && faqs.length === 0 && (
                    <Card elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2 }}>
                        <CardContent>
                            <Typography color="text.secondary">No FAQs are available yet. Please check back soon.</Typography>
                        </CardContent>
                    </Card>
                )}

                {!loading && !error && faqs.length > 0 && visibleFaqs.length === 0 && (
                    <Card elevation={0} sx={{ border: '1px solid #eee', borderRadius: 2 }}>
                        <CardContent>
                            <Typography color="text.secondary">
                                No FAQs match "{query}". Try a different search, or{' '}
                                <Typography
                                    component="a"
                                    onClick={() => navigate('/contact')}
                                    sx={{ color: TEAL, fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    contact us
                                </Typography>.
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {!loading && !error && grouped.map((group) => (
                    <Box key={group.category} sx={{ mb: 3 }}>
                        <Typography variant="overline" sx={{ color: TEAL, fontWeight: 700, letterSpacing: 1 }}>
                            {group.category}
                        </Typography>
                        {group.items.map((item) => (
                            <Accordion
                                key={item.faq_id}
                                elevation={0}
                                disableGutters
                                sx={{ border: '1px solid #eee', borderRadius: 2, mb: 1, '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography sx={{ fontWeight: 600 }}>{item.question}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                        {item.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                ))}
            </Container>
        </Box>
    );
};

export default FaqPage;
